import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Slice, Fragment, Node as ProseMirrorNode } from '@tiptap/pm/model';

export const PasteHandler = Extension.create({
  name: 'pasteHandler',

  addProseMirrorPlugins() {
    const { editor } = this;

    return [
      new Plugin({
        key: new PluginKey('pasteHandler'),
        props: {
          // Strip formatting from external HTML pastes (keep bold/italic only)
          transformPastedHTML(html: string) {
            // Create a temporary DOM to parse and clean
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const body = doc.body;

            // Walk all elements and strip everything except b/strong/i/em/u
            const allowedTags = new Set([
              'B', 'STRONG', 'I', 'EM', 'U', 'BR', 'P', 'DIV', 'SPAN',
            ]);

            function cleanNode(node: ChildNode): void {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const el = node as HTMLElement;
                // Remove all inline styles
                el.removeAttribute('style');
                el.removeAttribute('class');

                // If it's not an allowed tag, unwrap its children
                if (!allowedTags.has(el.tagName)) {
                  const parent = el.parentNode;
                  if (parent) {
                    while (el.firstChild) {
                      parent.insertBefore(el.firstChild, el);
                    }
                    parent.removeChild(el);
                    return;
                  }
                }

                // Clean children recursively
                Array.from(el.childNodes).forEach(cleanNode);
              }
            }

            Array.from(body.childNodes).forEach(cleanNode);
            return body.innerHTML;
          },

          // Plain text paste: wrap in dialogue nodes
          transformPastedText(text: string) {
            return text;
          },

          // Handle the paste slice — convert paragraphs to dialogue nodes
          transformPasted(slice: Slice) {
            const schema = editor.schema;
            const dialogueType = schema.nodes.dialogue;

            if (!dialogueType) return slice;

            const newNodes: ProseMirrorNode[] = [];
            slice.content.forEach((node) => {
              // Convert paragraph nodes to dialogue
              if (node.type.name === 'paragraph') {
                try {
                  const dialogueNode = dialogueType.create(null, node.content, node.marks);
                  newNodes.push(dialogueNode);
                } catch {
                  newNodes.push(node);
                }
              } else {
                newNodes.push(node);
              }
            });

            return new Slice(
              Fragment.from(newNodes),
              slice.openStart,
              slice.openEnd,
            );
          },
        },
      }),
    ];
  },
});
